import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Gift, Star, Trophy, Award, Ticket, Coffee } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RewardsPage = () => {
  const { token, user, updateUser } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const response = await fetch(`${API}/rewards`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setRewards(data.rewards || []);
          setUserPoints(data.points || 0);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRewards();
  }, [token]);

  const handleRedeem = async (rewardId) => {
    setRedeeming(rewardId);
    try {
      const response = await fetch(`${API}/rewards/redeem?reward_id=${rewardId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Reward redeemed! 🎉');
        // Refresh rewards
        const rewardsRes = await fetch(`${API}/rewards`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (rewardsRes.ok) {
          const data = await rewardsRes.json();
          setRewards(data.rewards || []);
          setUserPoints(data.points || 0);
          updateUser({ ...user, loyalty_points: data.points });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to redeem reward');
      }
    } catch (error) {
      toast.error('Failed to redeem reward');
    } finally {
      setRedeeming(null);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'booking': return Ticket;
      case 'refreshments': return Coffee;
      case 'equipment': return Award;
      case 'coaching': return Trophy;
      default: return Gift;
    }
  };

  const earnMethods = [
    { icon: Star, title: 'Book Courts', desc: '1 point per ₱50.00 spent on Court Bookings and Add-ons', color: 'text-yellow-500' },
    { icon: Trophy, title: 'Join Events', desc: '5 points per event', color: 'text-orange-500' },
    { icon: Award, title: 'Leave Reviews', desc: '2 points per review', color: 'text-blue-500' },
    { icon: Gift, title: 'Subscribe', desc: 'Earn bonus points when you subscribe', color: 'text-purple-500' }
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="rewards-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Loyalty Rewards</h1>
          <p className="text-muted-foreground">Earn points and redeem exclusive rewards</p>
        </div>

        {/* Points Balance */}
        <Card className="mb-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-center md:text-left mb-4 md:mb-0">
                <p className="text-muted-foreground mb-1">Your Points Balance</p>
                <div className="flex items-center justify-center md:justify-start">
                  <Gift className="h-8 w-8 mr-3 text-primary" />
                  <span className="text-5xl font-heading font-bold text-primary">{userPoints}</span>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-sm text-muted-foreground mb-2">Next Reward</p>
                <Progress value={(userPoints / 500) * 100} className="w-48 h-2 mb-1" />
                <p className="text-xs text-muted-foreground">{Math.max(0, 500 - userPoints)} points to go</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Earn */}
        <div className="mb-12">
          <h2 className="font-heading text-xl font-bold mb-4">How to Earn Points</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {earnMethods.map((method, idx) => (
              <Card key={idx} className="text-center">
                <CardContent className="p-4">
                  <div className={`h-10 w-10 rounded-full bg-muted flex items-center justify-center mx-auto mb-3`}>
                    <method.icon className={`h-5 w-5 ${method.color}`} />
                  </div>
                  <h4 className="font-semibold text-sm mb-1">{method.title}</h4>
                  <p className="text-xs text-muted-foreground">{method.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Available Rewards */}
        <div>
          <h2 className="font-heading text-xl font-bold mb-4">Available Rewards</h2>
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="h-48 bg-muted animate-pulse rounded-xl"></div>)}
            </div>
          ) : rewards.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rewards.map((reward) => {
                const Icon = getCategoryIcon(reward.category);
                const canRedeem = userPoints >= reward.points_required;
                
                return (
                  <Card 
                    key={reward.reward_id} 
                    className={`card-hover ${!canRedeem ? 'opacity-60' : ''}`}
                    data-testid={`reward-${reward.reward_id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <Badge variant="outline">{reward.category}</Badge>
                      </div>
                      <h3 className="font-heading font-semibold mb-1">{reward.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{reward.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-500 mr-1" />
                          <span className="font-bold">{reward.points_required}</span>
                          <span className="text-muted-foreground text-sm ml-1">pts</span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleRedeem(reward.reward_id)}
                          disabled={!canRedeem || redeeming === reward.reward_id}
                          className={canRedeem ? 'btn-primary' : ''}
                          data-testid={`redeem-${reward.reward_id}`}
                        >
                          {redeeming === reward.reward_id ? 'Redeeming...' : canRedeem ? 'Redeem' : 'Not Enough'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Gift className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No rewards available</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RewardsPage;
